// CONTRATO ÚNICO DE PERSONA — normaliza datos REALES (GET /api/personas[/{id}/ficha]) y datos DEMO
// (fixtures frontend) a la MISMA forma visual. La Persona es el centro; la alarma es sólo un estado.
import { verboEvento } from "@/lib/consolaV3/meta";
import { calcEdad, fmtTimestamp } from "@/lib/format";

export const COBERTURA_LABEL = {
  operativa: "Operativa", protegida: "Protegida", degradada: "Degradada", perdida: "Interrumpida",
  no_confirmable: "No confirmable", sin_cobertura: "Sin cobertura declarada",
};
export const COBERTURA_TONE = {
  operativa: "ok", protegida: "ok", degradada: "warn", perdida: "bad",
  no_confirmable: "warn", sin_cobertura: "muted",
};
const CAP_LABEL = { caida: "Caída", panico: "Pánico", presencia: "Presencia", audio: "Audio", cama: "Cama" };
const ORDEN_COB = { perdida: 4, degradada: 3, no_confirmable: 2, sin_cobertura: 1, operativa: 0, protegida: 0 };

// "hace ..." legible desde un ISO (autoridad = backend). Cada dato lleva SU propia antigüedad.
export function haceDesde(iso, nowMs = Date.now()) {
  if (!iso) return null;
  const s = Math.max(0, Math.floor((nowMs - new Date(iso).getTime()) / 1000));
  if (s < 60) return "hace segundos";
  if (s < 3600) return `hace ${Math.floor(s / 60)} min`;
  if (s < 86400) return `hace ${Math.floor(s / 3600)} h`;
  return `hace ${Math.floor(s / 86400)} d`;
}

const ESTADO_CASO_LABEL = {
  recibida: "Pendiente", tomada: "En atención", en_verificacion: "En verificación",
  no_verificable: "En atención", escalada: "Escalada",
};

function atencionFromApi(a) {
  if (!a) return null;
  return {
    alarma_id: a.alarma_id, es_tecnica: !!a.es_tecnica,
    verbo: verboEvento(a.tipo), prioridad: a.prioridad,
    prioridad_label: a.prioridad_label || a.prioridad,
    estado_label: ESTADO_CASO_LABEL[a.estado] || a.estado,
    en_atencion: !!a.en_atencion, operador: a.operador, desde: a.desde,
  };
}

function coberturaFromApi(cob = []) {
  return cob.map((c) => ({ capacidad: CAP_LABEL[c.capacidad] || c.capacidad, estado: c.estado }));
}

function observableFromApi(ob = {}, nowMs) {
  const items = [];
  for (const l of ob.lecturas || []) {
    items.push({ label: l.clave === "cama" ? "Cama" : "Presencia",
                 valor: verboEvento(l.tipo_evento), hace: haceDesde(l.hora, nowMs) });
  }
  for (const e of ob.ekh || []) {
    // Cada métrica conserva SU propia hora (núcleo por métrica). Una frecuencia sin lectura
    // utilizable se declara «Sin lectura»: nunca se publica como 0 ni se oculta la otra métrica.
    const hora = (k) => haceDesde(e[`${k}_hora`] || e.hora, nowMs);
    if (Number.isFinite(e.heart_rate) && e.heart_rate > 0) items.push({ label: "Frecuencia cardiaca", valor: `${e.heart_rate} lpm`, hace: hora("heart_rate") });
    else if (e.heart_rate_calidad === "sin_lectura") items.push({ label: "Frecuencia cardiaca", valor: "Sin lectura", hace: hora("heart_rate") });
    if (Number.isFinite(e.respiratory_rate) && e.respiratory_rate > 0) items.push({ label: "Respiración", valor: `${e.respiratory_rate} rpm`, hace: hora("respiratory_rate") });
    else if (e.respiratory_rate_calidad === "sin_lectura") items.push({ label: "Respiración", valor: "Sin lectura", hace: hora("respiratory_rate") });
    if (e.presence != null) items.push({ label: "Presencia", valor: e.presence ? "Detectada" : "Sin detección", hace: hora("presence") });
  }
  return { compartida: !!ob.compartida, sin_lectura: !!ob.sin_lectura, items };
}

function hechosFromApi(hs = [], nowMs) {
  return (hs || []).map((h) => {
    // C1 · todo hecho autorizado conserva su PROCEDENCIA: si ocurrió en otro centro autorizado se
    // rotula; sin sitio verificable no se atribuye al sitio actual.
    const origen = h.es_de_otro_sitio
      ? `Registrado en ${h.sitio_nombre || "otro centro autorizado"}`
      : h.sitio_verificable === false ? "Sin sitio verificable" : "";
    const conOrigen = (x) => (origen ? { ...x, sub: [x.sub, origen].filter(Boolean).join(" · ") } : x);
    if (h.clase === "contacto") {
      return conOrigen({ clase: "contacto", label: `Contacto${h.relacion ? ` · ${h.relacion}` : ""}`, sub: h.motivo || h.quien || "", hora: h.hora, hace: haceDesde(h.hora, nowMs) });
    }
    if (h.clase === "gestion_contacto") {
      const RES = { contactado: "Contactado", no_contesto: "No contestó", buzon: "Buzón", numero_equivocado: "Número equivocado", acudira_en_sitio: "Acudirá en sitio" };
      const MOT = { caida: "Caída", salida_cama: "Salida de cama", alerta_signos_vitales: "Alerta de signos vitales", otro: "Otro" };
      const nom = [h.contacto_nombre, h.relacion ? `(${h.relacion})` : ""].filter(Boolean).join(" ");
      const t = h.hora ? fmtTimestamp(h.hora) : "";
      return conOrigen({ clase: "gestion_contacto", label: `Contacto con ${nom || "familiar"}: ${RES[h.resultado] || h.resultado}`, sub: [MOT[h.motivo] || h.motivo, h.nota, h.operador, t].filter(Boolean).join(" · "), hora: h.hora, hace: haceDesde(h.hora, nowMs) });
    }
    if (h.clase === "caso") {
      const cerr = h.estado === "cerrada";
      return conOrigen({ clase: "caso", label: `${verboEvento(h.tipo)}${cerr ? " · caso cerrado" : " · caso"}`, sub: cerr ? (h.clasificacion || "") : (ESTADO_CASO_LABEL[h.estado] || ""), hora: h.hora, hace: haceDesde(h.hora, nowMs), prioridad: h.prioridad });
    }
    return conOrigen({ clase: "evento", label: verboEvento(h.tipo), sub: "", hora: h.hora, hace: haceDesde(h.hora, nowMs), prioridad: h.prioridad });
  });
}

// FICHA REAL -> contrato UI
export function fichaFromApi(d, nowMs = Date.now()) {
  const id = d.identidad || {};
  return {
    persona_id: id.id, demo: false,
    identidad: { nombre: id.nombre, edad: id.edad, modalidad: id.modalidad,
      modalidad_fuente: id.modalidad_fuente,
      idioma_preferido: id.idioma_preferido, telefono: id.telefono, estado: id.estado,
      habitacion: id.habitacion, cama_etiqueta: id.cama_etiqueta, zona_tipo: id.zona_tipo,
      sitio_nombre: id.sitio_nombre, ciudad: id.ciudad, direccion: id.direccion, zona_nombre: id.zona_nombre },
    // GC94: el backend ya entregaba las indicaciones y el contexto de atención; el adaptador los
    // descartaba y la ficha quedaba sin información útil para enfermería.
    instrucciones: d.instrucciones || (d.atencion_contexto || {}).indicaciones || "",
    atencion_contexto: d.atencion_contexto,
    plan_vigente: d.plan_vigente,
    dispositivos: d.dispositivos,
    readiness: d.readiness,
    atencion: atencionFromApi(d.atencion),
    cobertura: coberturaFromApi(d.cobertura),
    observable: observableFromApi(d.observable, nowMs),
    ultimos_hechos: hechosFromApi(d.ultimos_hechos, nowMs),
    // P5 · el adaptador es el ÚNICO que puede producir el centinela "error"; conserva `undefined`
    // (no entregado por alcance) y trata []/null/"" como vacío REGISTRADO, sin lanzar TypeError.
    contactos: d.contactos === undefined ? undefined
      : Array.isArray(d.contactos)
        ? d.contactos.map((c) => ({
          orden: c.orden, relacion: c.relacion, autoridad: c.autoridad,
          nivel_autoridad: c.nivel_autoridad, nivel_autoridad_origen: c.nivel_autoridad_origen,
          nombre: c.nombre, telefono: c.telefono, funcion: c.funcion,
          puede_acudir: c.puede_acudir, restriccion_horaria: c.restriccion_horaria,
          recibe_avisos: c.recibe_avisos, puede_confirmar_estado: c.puede_confirmar_estado,
        }))
        : d.contactos === null || d.contactos === "" ? []
          : "error",
    contexto_habitacion: d.contexto_habitacion,
  };
}

// TARJETA de población REAL -> contrato UI
export function tarjetaFromApi(p) {
  const caso = p.caso_activo;
  return {
    id: p.id, nombre: p.nombre || "(sin nombre)", edad: p.edad,
    sitio_nombre: p.sitio_nombre, ciudad: p.ciudad, direccion: p.direccion,
    zona_nombre: p.zona_nombre, modalidad: p.modalidad,
    atencion: caso ? { alarma_id: caso.alarma_id, verbo: verboEvento(caso.tipo), prioridad: caso.prioridad, en_atencion: caso.en_atencion, operador: caso.operador } : null,
    cobertura_peor: p.cobertura_peor,
    ultimo_hecho: p.ultimo_hecho ? { label: verboEvento(p.ultimo_hecho.tipo), hace: haceDesde(p.ultimo_hecho.hora) } : null,
  };
}

export function coberturaPeorLabel(estado) {
  return { operativa: "Operativa", protegida: "Protegida", degradada: "Cobertura degradada",
    perdida: "Cobertura interrumpida", no_confirmable: "No confirmable",
    sin_cobertura: "Sin cobertura declarada" }[estado] || estado;
}

export { calcEdad };
